import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export interface Breadcrumb {
  /** Display label, already translated. */
  label: string;
  /** Optional route. If omitted, the segment renders as plain text (terminal segment). */
  to?: string;
}

interface BreadcrumbContextValue {
  crumbs: Breadcrumb[];
  setCrumbs: (crumbs: Breadcrumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  crumbs: [],
  setCrumbs: () => {},
});

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [crumbs, setCrumbsState] = useState<Breadcrumb[]>([]);

  const setCrumbs = useCallback((next: Breadcrumb[]) => {
    setCrumbsState(prev => {
      if (prev.length !== next.length) return next;
      for (let i = 0; i < next.length; i++) {
        if (prev[i].label !== next[i].label || prev[i].to !== next[i].to) return next;
      }
      return prev;
    });
  }, []);

  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs, setCrumbs]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

/** Read the current breadcrumb list (e.g. for the TopBar). */
export function useBreadcrumbs(): Breadcrumb[] {
  return useContext(BreadcrumbContext).crumbs;
}

/** Set the breadcrumbs for the current page. Clears on unmount. */
export function useBreadcrumb(crumbs: Breadcrumb[]): void {
  const { setCrumbs } = useContext(BreadcrumbContext);
  // Stable string key so useEffect doesn't re-run on identity changes.
  const key = crumbs.map(c => `${c.label}|${c.to ?? ''}`).join(';');
  useEffect(() => {
    setCrumbs(crumbs);
    return () => setCrumbs([]);
    // `crumbs` is intentionally excluded: `key` is its stable string projection, so the
    // effect re-runs only on a real trail change, not on every array-identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setCrumbs]);
}
